import React, { useEffect, useState } from 'react';
import { Download, Share, Plus, X, Smartphone } from 'lucide-react';
import { api } from '@/lib/api';
import { installBus } from '@/lib/installBus';

/* Browsers that fire beforeinstallprompt (Chrome/Edge Android & desktop) */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

import type { InstallState } from '@/lib/installBus';

const DISMISS_KEY = 'ek-install-dismissed-v1';
const SNOOZE_DAYS = 14;

const detectPlatform = (): 'ios' | 'android' | 'windows' | 'mac' | 'other' => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Mac/i.test(ua)) return 'mac';
  return 'other';
};

const isStandalone = (): boolean => {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari
  return (window.navigator as any).standalone === true;
};

const isSnoozed = (): boolean => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const { at } = JSON.parse(raw);
    const age = (Date.now() - new Date(at).getTime()) / (1000 * 60 * 60 * 24);
    return age < SNOOZE_DAYS;
  } catch {
    return false;
  }
};

const snooze = () => {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify({ at: new Date().toISOString() })); } catch { /* ignore */ }
};

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'windows' | 'mac' | 'other'>('other');

  useEffect(() => {
    setPlatform(detectPlatform());
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      installBus.set({ status: 'installed' });
      try {
        if (!localStorage.getItem('ek-install-logged-v1')) {
          api.post('/public/install').catch(() => {});
          localStorage.setItem('ek-install-logged-v1', JSON.stringify({ at: new Date().toISOString() }));
        }
      } catch { /* ignore */ }
    };

    if (isStandalone()) {
      onInstalled();
      return;
    }
    const snoozed = isSnoozed();

    const handler = (e: Event) => {
      e.preventDefault();
      const ev = e as BeforeInstallPromptEvent;
      setDeferred(ev);
      installBus.set({
        status: 'available',
        promptNow: async () => {
          try {
            await ev.prompt();
            const choice = await ev.userChoice;
            if (choice.outcome === 'dismissed') snooze();
            setDeferred(null);
            setVisible(false);
            installBus.set({ status: 'idle' });
          } catch { /* ignore */ }
        },
      });
      if (!snoozed) window.setTimeout(() => setVisible(true), 2400);
    };
    window.addEventListener('beforeinstallprompt', handler as any);
    window.addEventListener('appinstalled', onInstalled);

    const platformNow = detectPlatform();
    let iosTimer: number | undefined;
    if (platformNow === 'ios') {
      installBus.set({ status: 'ios' } as InstallState);
      iosTimer = window.setTimeout(() => {
        if (!isStandalone() && !isSnoozed()) setVisible(true);
      }, 6000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler as any);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, []);

  useEffect(() => {
    const openIos = () => {
      if (detectPlatform() === 'ios') setIosGuideOpen(true);
    };
    window.addEventListener('ek-show-ios-install', openIos);
    return () => window.removeEventListener('ek-show-ios-install', openIos);
  }, []);

  const dismiss = () => {
    snooze();
    setVisible(false);
    setIosGuideOpen(false);
  };

  const install = async () => {
    if (deferred) {
      try {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === 'dismissed') snooze();
        setDeferred(null);
        setVisible(false);
      } catch {
        setVisible(false);
      }
      return;
    }
    // iOS fallback — show manual guide
    setIosGuideOpen(true);
  };

  if (!visible && !iosGuideOpen) return null;

  const title =
    platform === 'ios' ? 'Add Ekuba to your Home Screen'
      : platform === 'android' ? 'Install Ekuba on your phone'
      : platform === 'windows' ? 'Install Ekuba as a Windows app'
      : platform === 'mac' ? 'Add Ekuba to your Dock'
      : 'Install Ekuba';

  const subtitle = platform === 'ios'
    ? 'Launch straight from your Home Screen with no Safari bars.'
    : 'Full-screen player with offline access to the hero image, logo and press kit shell.';

  return (
    <>
      {visible && !iosGuideOpen && (
        <div className="ek-install" role="dialog" aria-labelledby="ek-install-title" aria-describedby="ek-install-desc" data-testid="install-prompt">
          <span className="ek-install-icon" aria-hidden="true">
            <Smartphone size={16} />
          </span>
          <div className="ek-install-text">
            <strong id="ek-install-title">{title}</strong>
            <span id="ek-install-desc">{subtitle}</span>
          </div>
          <div className="ek-install-actions">
            <button type="button" className="ek-install-btn" onClick={install} data-testid="install-prompt-accept">
              <Download size={14} /> {platform === 'ios' ? 'Show how' : 'Install'}
            </button>
            <button type="button" className="ek-install-close" onClick={dismiss} aria-label="Dismiss" data-testid="install-prompt-dismiss">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {iosGuideOpen && (
        <div className="ek-install-modal-backdrop" onClick={dismiss} role="dialog" aria-labelledby="ek-ios-guide-title" data-testid="install-ios-guide">
          <div className="ek-install-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ek-install-modal-head">
              <h3 id="ek-ios-guide-title">Add to Home Screen</h3>
              <button type="button" onClick={dismiss} aria-label="Close" className="ek-install-close">
                <X size={16} />
              </button>
            </div>
            <ol className="ek-install-steps">
              <li>
                Tap <strong className="ek-install-pill"><Share size={14} /> Share</strong> in the Safari toolbar below.
              </li>
              <li>
                Scroll down and choose{' '}
                <strong className="ek-install-pill"><Plus size={14} /> Add to Home Screen</strong>.
              </li>
              <li>Tap <strong>Add</strong>. The Ekuba icon lands on your Home Screen.</li>
            </ol>
            <p className="ek-install-hint">Works the same way on iPad (Safari) and in Chrome on Mac (Share → Install).</p>
          </div>
        </div>
      )}
    </>
  );
}
